/* oxlint-disable typescript/ban-ts-comment */
import axios from 'utils/axios'
import { Methods, Response } from 'types/request'
import { AxiosError, AxiosRequestConfig } from 'axios'
import { BOLT_BACKEND_URL } from 'constants/envs.ts'
import { UNKNOWN_REQUEST_ERROR_CODE } from 'constants/errors.ts'

export const newRequest = async <T = undefined>(options: AxiosRequestConfig): Promise<Response<T>> => {
  try {
    const {
      data: { data, error, message, success }
    } = await axios(options)

    return { data, error, message, success }
  } catch (err) {
    const { response } = err as AxiosError

    if (response) {
      const body = response.data && typeof response.data === 'object' ? (response.data as Response<T>) : undefined

      // A gateway, proxy or crashed handler answers with a body that is not the API envelope (empty, plain text, HTML),
      // and an envelope naming neither a message nor a code is equally unactionable. Name the status in both cases so
      // the failure is identifiable and splits per status.
      if (!body || (!body.message && !body.error && !body.errorCode)) {
        return { ...body, success: false, errorCode: `HTTP_${response.status}` }
      }

      return body
    }

    // No `response` means the request never reached the backend, so there is no error body to report.
    return {
      success: false,
      message: 'ApiError',
      errorCode: (err as AxiosError).code ?? UNKNOWN_REQUEST_ERROR_CODE
    }
  }
}

/**
 * Make a request with the given options
 *
 * @param options
 */
const request = async <TResponse = any, TMessage = any>(
  options: AxiosRequestConfig
): Promise<{
  success: boolean
  data?: TResponse
  message?: TMessage
}> => {
  try {
    const { data: responseData } = await axios(options)

    return {
      success: true,
      data: responseData
    }
  } catch (err) {
    return {
      success: false,
      // @ts-ignore
      message: err.response ? err.response.data : 'ApiError'
    }
  }
}

export const getAxiosRequestConfig = (
  method: Methods,
  url: string,
  data: Record<string, unknown> | null = null,
  params: Record<string, unknown> | null = null,
  headers: Record<string, string> | null = null
): AxiosRequestConfig => ({
  method,
  url,
  ...(data !== null && { data }),
  ...(params !== null && { params }),
  ...(headers !== null && { headers })
})

export const getAxiosRequestConfigBoltBackend = (
  method: Methods,
  url: string,
  data: Record<string, unknown> | null = null,
  params: Record<string, unknown> | null = null,
  headers: Record<string, string> | null = null
): AxiosRequestConfig => ({
  ...getAxiosRequestConfig(method, url, data, params, headers),
  baseURL: BOLT_BACKEND_URL
})

export default request
